import { useState } from "react";
import { ActionItem, ActionItemStatus, ActionItemFormData } from "../types/actionItems";
import { actionItemsService } from "../services/supabase/action-items";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

export function useActionItemOperations(meetingId: string, userId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const createActionItem = async (formData: ActionItemFormData) => {
    if (!userId) {
      toast.error("User ID is required");
      return;
    }

    setIsLoading(true);
    try {
      const newItem: Omit<ActionItem, "id" | "user_id"> = {
        meeting_id: meetingId,
        title: formData.title,
        description: formData.description,
        due_date: formData.due_date,
        status: formData.status,
        is_completed: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        assignee: null,
        priority: null,
        reference: null,
        instructions: null,
        context: null,
        additional_notes: null
      };

      const data = await actionItemsService.createActionItem(userId, newItem);

      queryClient.invalidateQueries({ queryKey: ['actionItems'] });
      toast.success("Action item created successfully");
      return data;
    } catch (error) {
      toast.error("Failed to create action item");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateActionItem = async (actionItemId: string, updates: Partial<ActionItem>) => {
    if (!userId) {
      toast.error("User ID is required");
      return;
    }

    setIsLoading(true);
    try {
      const data = await actionItemsService.updateActionItem(actionItemId, userId, updates);
      queryClient.invalidateQueries({ queryKey: ['actionItems'] });
      toast.success("Action item updated successfully");
      return data;
    } catch (error) {
      toast.error("Failed to update action item");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteActionItem = async (actionItemId: string) => {
    if (!userId) {
      toast.error("User ID is required");
      return;
    }

    setIsLoading(true);
    try {
      await actionItemsService.deleteActionItem(actionItemId, userId);
      queryClient.invalidateQueries({ queryKey: ['actionItems'] });
      toast.success("Action item deleted successfully");
      return true;
    } catch (error) {
      toast.error("Failed to delete action item");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (actionItemId: string, status: ActionItemStatus) => {
    if (!userId) {
      toast.error("User ID is required");
      return;
    }

    setIsLoading(true);
    try {
      await actionItemsService.updateActionItem(actionItemId, userId, { status });
      queryClient.invalidateQueries({ queryKey: ['actionItems'] });
      toast.success("Action item status updated successfully");
      return true;
    } catch (error) {
      toast.error("Failed to update action item status");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    createActionItem,
    updateActionItem,
    deleteActionItem,
    updateStatus,
  };
}